'use client'

import { GridSection } from '@/components/marketing/fragments/grid-section'
import { MediaQueries } from '@/constants/media-queries'
import { useMediaQuery } from '@/hooks/use-media-query'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { motion } from 'framer-motion'
import Image from 'next/image'
import AppIcon from 'public/marketing/services/app.png'
import BlockchainIcon from 'public/marketing/services/blockchain.png'
import MetaverseIcon from 'public/marketing/services/metaverse.png'
import SortwareIcon from 'public/marketing/services/sortware.png'
import UXUIIcon from 'public/marketing/services/uxui.png'
import WebIcon from 'public/marketing/services/web.png'
import * as React from 'react'
import { SiteHeading } from '../fragments/site-heading'

const itemFadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 }
  }
}

const servicesData = [
  {
    icon: BlockchainIcon,
    title: msg`IoT, AI & Blockchain`,
    description: msg`Developing IoT connectivity, artificial intelligence, and Blockchain integration into daily life.`
  },
  {
    icon: AppIcon,
    title: msg`Application Development`,
    description: msg`Building high-performance, optimized cross-platform applications like Mobile Apps, Web Apps, and Desktop Apps.`
  },
  {
    icon: SortwareIcon,
    title: msg`Game Development`,
    description: msg`Creating games for Web and Mobile platforms with integrated Blockchain technology.`
  },
  {
    icon: MetaverseIcon,
    title: msg`Digital Transformation`,
    description: msg`Consulting and implementing feasible, effective digital transformation solutions.`
  },
  {
    icon: UXUIIcon,
    title: msg`UI/UX Design & Branding`,
    description: msg`Designing professional user interfaces, user experiences, and brand identities.`
  },
  {
    icon: WebIcon,
    title: msg`Website Development`,
    description: msg`Building content management, e-commerce, and various other types of websites.`
  }
]

export function Services(): React.JSX.Element {
  const { i18n } = useLingui()
  const isMobile = useMediaQuery(MediaQueries.SmDown, { ssr: true, fallback: true })

  return (
    <GridSection hideVerticalGridLines={!isMobile}>
      <section id='services' className='w-full py-12 md:py-24'>
        <div className='container px-4 md:px-6 rounded-3xl'>
          <SiteHeading
            badge={msg`Services`}
            title={msg`What we offer`}
            description={msg`Comprehensive technology solutions for businesses in the digital era`}
          />
          <div className='mx-auto grid max-w-5xl items-center gap-3 py-12 md:grid-cols-2 lg:grid-cols-3'>
            {servicesData.map(({ title, description, icon }) => (
              <motion.div
                key={i18n._(title)}
                variants={itemFadeIn}
                className='group relative overflow-hidden duration-300 hover:-translate-y-2 rounded-3xl border p-6 shadow-sm transition-all hover:shadow-md bg-background/80'
              >
                <div className='absolute -right-20 -top-20 h-40 w-40 rounded-full bg-orange-400/10 group-hover:bg-orange-400/20 transition-all duration-300'></div>
                <div className='relative space-y-3'>
                  <Image
                    src={icon}
                    alt={i18n._(title)}
                    className='min-h-20'
                    width={130}
                    height={182}
                  />
                  <h3 className='text-xl font-bold'>{i18n._(title)}</h3>
                  <p className='text-muted-foreground'>{i18n._(description)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </GridSection>
  )
}
